/**
 * ReviewPage.tsx — Second page of the taskpane.
 *
 * Owns all review-scoped state (category/style selection, paragraph
 * data, review-action trigger and derived change counts). Displays
 * a toolbar (Back + Refresh), the paragraph review pane, and the
 * style details summary.
 */

import React from "react";
import { Button } from "@fluentui/react-components";
import { ArrowLeft20Regular, ArrowSync20Regular } from "@fluentui/react-icons";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGetCharacterStyleRecordsQuery,
  useGetDivisionTextQuery,
  useGetParagraphsQuery,
  useGetStyleRecordsQuery,
} from "src/api/word-api";
import { Outline, Review } from "src/components";
import { useReviewContext } from "src/providers";
import { useAppSelector } from "src/hooks";
import {
  selectCharacterModeOutline,
  selectIsMultiselect,
  selectNextDisabled,
  selectOutlineSortMode,
  selectParagraphInfo,
  selectPreviousDisabled,
  selectSelectedItem,
  selectSelectionBounds,
  selectSelectionNotInProfile,
  selectSortedOutline,
  selectTotal,
  selectFilteredTotalTracked,
  selectSelectedItemTracked,
  selectNoSelectedCharacterStyle,
} from "src/features/selectors";
import "./styles/ReviewPage.css";

interface ReviewPageProps {
  onNavigateToStyling: () => void;
}

export const ReviewPage: React.FC<ReviewPageProps> = ({ onNavigateToStyling }) => {
  const {
    handleApprove,
    handleApproveAll,
    handleRestyle,
    handleRestyleAll,
    handleClearCharacterStyle,
    handleClearGroup,
    handleClearStyle,
    handleNext,
    handlePrevious,
    handleSelectCharacterStyle,
    handleSelectGroup,
    handleSelectStyle,
    handleSetBoundaryIndices,
    handleSetIgnoreParagraphs,
    handleSetOutlineMode,
    handleSetOutlineSortMode,
    handleSetReviewMode,
  } = useReviewContext();

  const boundaryIndices = useAppSelector((state) => state.review.boundaryIndices);
  const characterModeOutline = useAppSelector(selectCharacterModeOutline);
  const filteredTotalTracked = useAppSelector(selectFilteredTotalTracked);
  const groups = useAppSelector(selectSortedOutline);
  const ignoreParagraphs = useAppSelector((state) => state.review.ignoreParagraphs);
  const isMultiSelect = useAppSelector(selectIsMultiselect);
  const nextDisabled = useAppSelector(selectNextDisabled);
  const noSelectedCharacterStyle = useAppSelector(selectNoSelectedCharacterStyle);
  const outlineMode = useAppSelector((state) => state.review.outlineMode);
  const outlineSortMode = useAppSelector(selectOutlineSortMode);
  const paragraphInfo = useAppSelector(selectParagraphInfo);
  const previousDisabled = useAppSelector(selectPreviousDisabled);
  const selectedCharacterStyleFilter = useAppSelector(
    (state) => state.review.selectedCharacterStyleFilter
  );
  const selectedCharacterOriginFilter = useAppSelector(
    (state) => state.review.selectedCharacterOriginFilter
  );
  const selectedGroupFilter = useAppSelector((state) => state.review.selectedGroupFilter);
  const selectedItem = useAppSelector(selectSelectedItem);
  const selectedItemTracked = useAppSelector(selectSelectedItemTracked);
  const selectedStyleFilter = useAppSelector((state) => state.review.selectedStyleFilter);
  const selectionBounds = useAppSelector(selectSelectionBounds);
  const selectionNotInProfile = useAppSelector(selectSelectionNotInProfile);
  const characterStyles = useAppSelector((state) => state.review.styleProfile?.character_styles);
  const styleDefinitionMap = useAppSelector((state) => state.review.styleDefinitionMap);
  const total = useAppSelector(selectTotal);

  const divisionTextResults = useGetDivisionTextQuery(paragraphInfo ?? skipToken);
  const paragraphsResult = useGetParagraphsQuery();
  const styleRecordsResult = useGetStyleRecordsQuery();
  const charStyleRecordsResult = useGetCharacterStyleRecordsQuery();

  const isLoading = React.useMemo(
    () =>
      divisionTextResults.isFetching ||
      divisionTextResults.isLoading ||
      paragraphsResult.isFetching ||
      paragraphsResult.isLoading ||
      styleRecordsResult.isFetching ||
      styleRecordsResult.isLoading ||
      charStyleRecordsResult.isFetching ||
      charStyleRecordsResult.isLoading,
    [divisionTextResults, paragraphsResult, styleRecordsResult, charStyleRecordsResult]
  );

  return (
    <div className="review-page">
      <div className="review-page__toolbar">
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowLeft20Regular />}
          onClick={onNavigateToStyling}
        >
          Back to Styling
        </Button>
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowSync20Regular />}
          onClick={() => {
            paragraphsResult.refetch();
            styleRecordsResult.refetch();
            charStyleRecordsResult.refetch();
          }}
          aria-label="Refresh review data"
          style={{ marginRight: "1rem" }}
        >
          Refresh
        </Button>
      </div>

      <fieldset style={{ border: 0, margin: "0 0 16px 0", padding: 0 }}>
        <Review
          ignoreParagraphs={ignoreParagraphs}
          isMultiSelect={isMultiSelect}
          loading={isLoading}
          noSelectedCharacterStyle={noSelectedCharacterStyle}
          notInProfile={selectionNotInProfile}
          nextDisabled={nextDisabled}
          outlineMode={outlineMode}
          previousDisabled={previousDisabled}
          selectionBounds={selectionBounds}
          selectedCharacterStyleFilter={selectedCharacterStyleFilter}
          selectedGroupFilter={selectedGroupFilter}
          selectedItem={selectedItem}
          selectedStyleFilter={selectedStyleFilter}
          total={total}
          totalTracked={filteredTotalTracked}
          tracked={selectedItemTracked}
          onApprove={handleApprove}
          onApproveAll={handleApproveAll}
          onClearCharacterStyle={handleClearCharacterStyle}
          onClearGroup={handleClearGroup}
          onClearStyle={handleClearStyle}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onRestyle={handleRestyle}
          onRestyleAll={handleRestyleAll}
          setIgnoreParagraphs={handleSetIgnoreParagraphs}
        />
      </fieldset>

      <fieldset style={{ border: 0, margin: "0 0 16px 0", padding: 0 }}>
        <div>
          <Outline
            characterStyles={characterStyles}
            groups={groups}
            ignoreParagraphs={ignoreParagraphs}
            outlineMode={outlineMode}
            outlineSortMode={outlineSortMode}
            selectedBoundaryIndices={boundaryIndices}
            selectedCharacterOriginFilter={selectedCharacterOriginFilter}
            selectedCharacterStyleFilter={selectedCharacterStyleFilter}
            selectedGroupFilter={selectedGroupFilter}
            selectedStyleFilter={selectedStyleFilter}
            styleDefinitionMap={styleDefinitionMap}
            styleGroups={characterModeOutline}
            onChangeReviewMode={handleSetReviewMode}
            onClearCharacterStyle={handleClearCharacterStyle}
            onClearGroup={handleClearGroup}
            onClearStyle={handleClearStyle}
            onSelectCharacterStyle={handleSelectCharacterStyle}
            onSelectGroup={handleSelectGroup}
            onSelectOutlineSortMode={handleSetOutlineSortMode}
            onSelectOutlineMode={handleSetOutlineMode}
            onSelectStyle={handleSelectStyle}
            onSetBoundaryIndices={handleSetBoundaryIndices}
          />
        </div>
      </fieldset>
    </div>
  );
};
